import React, { Fragment } from 'react';
import Dropdown from 'react-accessible-dropdown';
import 'react-accessible-dropdown/style.css';
import classNames from 'classnames';
import { __ } from '@wordpress/i18n';
import { RawHTML } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import ReactDOM from 'react-dom';
import DOMPurify from 'dompurify';
import ReactHtmlParser from 'react-html-parser';
import './personListing.scss';

const appElement = document.querySelector('.person-listing');

const defaultDepartment = {
  label: __('All departments', 'kauffman'),
  value: 0,
};
const defaultAlphaFilter = {
  value: '',
  label: __('All Last Names', 'kauffman'),
};

class PersonListing extends React.Component {
  /**
   * Helper for name sorting.
   *
   * @param n1
   * @param n2
   * @returns {number}
   */
  static sortNames(n1, n2) {
    if (n1 > n2) { return + 1; }
    if (n1 < n2) { return - 1; }
    return 0;
  }

  /**
   * Constructor. Binds function scope.
   * @param {object} props - Props for this component.
   */
  constructor(props) {
    super(props);
    this.state = {
      activeAlphaFilter: defaultAlphaFilter,
      activeDepartment: defaultDepartment,
      loading: true,
      personList: [],
      departmentList: [],
      filtersLoaded: false,
    };
    this.loadFromHash = this.loadFromHash.bind(this);
    this.onDepartmentActivate = this.onDepartmentActivate.bind(this);
    this.onAlphaFilterActivate = this.onAlphaFilterActivate.bind(this);

    // Get array of last names and set up chunks for alpha dropdown.
    this.alphaFilterChunks = [
      {
        value: 'ABCDE',
        label: 'A-E',
      },
      {
        value: 'FGHIJ',
        label: 'F-J',
      },
      {
        value: 'KLMNO',
        label: 'K-O',
      },
      {
        value: 'PQRST',
        label: 'P-T',
      },
      {
        value: 'UVWXYZ',
        label: 'U-Z',
      },
    ];
    // Add blank option to beginning of options array.
    this.alphaFilterChunks.unshift(defaultAlphaFilter);
  }

  /**
   * On component mount, fire off all the necessary API requests for
   * persons, departments and types.
   */
  componentDidMount() {
    this.mounted = true;
    let totalPersonPages;

    // Get the last portion of the url.
    // phpcs:ignore WordPressVIPMinimum.JS.Window.VarAssignment
    const pathArray = window.location.pathname.split('/');
    const exclusions = ['', 'our-people', 'about', 'leadership-and-associates'];

    // Filter empty strings from array.
    const filteredPathArray = 0 < pathArray.length
      ? pathArray.filter((path) => ! exclusions.includes(path)) : []; // eslint-disable-line

    // Produce '' if we're only on the
    // landing page for leadership-and-associates.
    // Check that our array length is greater than
    const activeType = 0 < filteredPathArray.length
      ? filteredPathArray.reverse().find((portion) => portion)
      : '';

    const typeParams = activeType ? `?slug=${activeType}` : '';
    const thePath = `/wp-json/wp/v2/person-types${typeParams}`;

    // Get the person type that matches the active type set in the URL.
    this.fetchCurrentTypes = apiFetch({ path: thePath }).then((data) => {
      if (this.mounted) {
        // We probably won't have a view in this app that shows all
        // people in all types, so we can safely use the first item.
        const activeTypeId = data[0].id;
        const typeIdParam = activeTypeId ?
          `&person-types=${activeTypeId}` :
          '';
        // Fetch list of people to display.
        this.fetchCurrentPersons =
          fetch(`/wp-json/wp/v2/person?page=1&per_page=100${typeIdParam}&orderby=id&order=asc`) // eslint-disable-line max-len
            .then((response) => {
              totalPersonPages = parseInt(response.headers.get('X-WP-TotalPages'), 10); // eslint-disable-line max-len
              return response.json();
            })
            .then((personData) => {
              this.setPersonData(personData);

              // Get the rest of the data
              [...Array(totalPersonPages - 1).keys()].forEach((page) => {
                apiFetch({
                  path: `/wp-json/wp/v2/person?page=${page + 2}${typeIdParam}&per_page=100&orderby=id&order=asc`, // eslint-disable-line max-len
                })
                  .then((morePersonData) => {
                    this.setPersonData(morePersonData);
                  });
              });

              this.loadFromHash();
            }).catch(() => {
              this.setState({
                loading: false,
                personList: [],
              });
            });
      }
    }).catch(() => {
      if (this.mounted) {
        this.setState({
          loading: false,
          personList: [],
        });
      }
    });

    // Fetch and set department list.
    this.fetchDepartmentList = fetch('/wp-json/wp/v2/person-departments?per_page=50') // eslint-disable-line max-len
      .then((response) => response.json())
      .then((data) => {
        if (this.mounted) {
          const departmentsFormatted = data.filter((item) => 0 !== item.count);
          this.setState({
            departmentList: departmentsFormatted.map((department) => ({
              label: department.name,
              value: department.id,
              slug: department.slug,
            })),
          });
        }
      }).catch(() => {
        if (this.mounted) {
          this.setState({
            departmentList: [],
          });
        }
      });
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  /**
   * Set active alphabet filter based on selection.
   * Occurs on load and on dropdown click.
   * @param object alphaFilter The alpha filter to activate.
   */
  onAlphaFilterActivate(alphaFilter) {
    if (
      alphaFilter &&
      alphaFilter.label &&
      alphaFilter.label !== defaultAlphaFilter.label
    ) {
      this.setState({
        activeAlphaFilter: {
          label: alphaFilter.label,
          value: alphaFilter.value,
        },
      });
    } else {
      this.setState({
        activeAlphaFilter: defaultAlphaFilter,
      });
    }
  }

  /**
   * Set active department state based on selection.
   * Occurs on load and on dropdown click.
   * @param object department The department object to activate.
   */
  onDepartmentActivate(department) {
    if (
      department &&
      department.label !== defaultDepartment.label
    ) {
      this.setState({
        activeDepartment: {
          label: department.label,
          value: department.value,
          slug: this.state.departmentList
            .find((item) => item.value === department.value).slug,
        },
      });
    } else {
      this.setState({
        activeDepartment: defaultDepartment,
      });
    }
  }

  /**
   * Update existing app state with incoming REST data
   * @param {object} data - Incoming person data
   */
  setPersonData(data) {
    const {
      personList = [],
    } = this.state;
    this.setState({
      personList: [
        ...personList,
        ...data,
      ],
      loading: false,
    });
  }

  pushToHash() {
    if (! this.state.filtersLoaded) {
      return;
    }

    const {
      activeAlphaFilter,
      activeDepartment,
    } = this.state;
    const query = {};

    if (activeAlphaFilter.value) {
      query.alphaFilter = activeAlphaFilter.value;
    }

    if (activeDepartment.slug) {
      query.department = activeDepartment.slug;
    }

    const queryString = new URLSearchParams(query).toString();

    window.history.pushState(
      query,
      'People',
      queryString ? `#${queryString}` : window.location.pathname,
    );
  }

  loadFromHash() {
    const query = new URLSearchParams(window.location.hash.slice(1));

    if (query.has('alphaFilter')) {
      const filter = query.get('alphaFilter');

      this.setState({
        activeAlphaFilter: {
          label: this.alphaFilterChunks
            .find((item) => item.value === filter).label,
          value: filter,
        },
      });
    }

    if (query.has('department')) {
      const filter = query.get('department');

      this.setState({
        activeDepartment: {
          label: this.state.departmentList
            .find((item) => item.slug === filter).label,
          value: this.state.departmentList
            .find((item) => item.slug === filter).value,
          slug: filter,
        },
      });
    }

    this.setState({ filtersLoaded: true });
  }

  /**
   * Renders component markup.
   * @returns {object} - JSX for this component.
   */
  render() {
    const {
      activeAlphaFilter,
      activeDepartment,
      loading,
      personList,
      departmentList,
    } = this.state;
    this.pushToHash();

    if (loading) {
      return (
        <div className="person__listing--loading">
          <svg
            height="50"
            stroke="#3579bd"
            viewBox="0 0 50 50"
            width="50"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g fill="none" fillRule="evenodd">
              <g transform="translate(1 1)" strokeWidth="2">
                <circle strokeOpacity=".5" cx="18" cy="18" r="18" />
                <path d="M36 18c0-9.94-8.06-18-18-18">
                  <animateTransform
                    attributeName="transform"
                    dur="1s"
                    from="0 18 18"
                    repeatCount="indefinite"
                    to="360 18 18"
                    type="rotate"
                  />
                </path>
              </g>
            </g>
          </svg>
        </div>
      );
    }

    // When there are no departments or people, don't render anything.
    if (
      (! departmentList && ! personList)
      || ! appElement
      || 0 === personList.length
    ) {
      return false;
    }

    // Get possible departments and prepend default option.
    const departmentsOptions = departmentList.map((department) => ({
      label: ReactHtmlParser(department.label),
      value: department.value,
    }));
    // Add blank option to beginning of options array.
    departmentsOptions.unshift(defaultDepartment);

    // Deep copy to avoid side effects.
    const personsCopy = personList.map((person) => ({ ...person }));

    // Create the set of persons with filters applied.
    const filteredPersons = personsCopy.filter((person) => {
      // eslint-disable-next-line dot-notation
      const matchingDepartments = person['person-departments']
        .filter((departmentId) => (
          departmentId === activeDepartment.value
        ));

      // if person.meta.kauffman_person_last_name[0] is contained
      // in the string within chunks array
      const chunkContains = person.meta.kauffman_person_last_name
        ? - 1 < activeAlphaFilter.value
          .indexOf(person.meta.kauffman_person_last_name[0][0])
        : false;

      // If default options are active, return the person by default.
      // Return person if their departments
      // contain person with matching active value.
      if (
        defaultDepartment.label === activeDepartment.label &&
        defaultAlphaFilter.label === activeAlphaFilter.label
      ) {
        return true;
      } else if (
        (
          defaultAlphaFilter.label === activeAlphaFilter.label &&
          0 < matchingDepartments.length
        ) || (
          defaultDepartment.label === activeDepartment.label &&
          chunkContains
        ) || (
          0 < matchingDepartments.length &&
          chunkContains
        )
      ) {
        return true;
      }

      return false;
    });

    // Sort people alphabetically.
    filteredPersons.sort((item1, item2) => ((
      item1.meta.kauffman_person_last_name &&
      item2.meta.kauffman_person_last_name
    ) ? (
        PersonListing.sortNames(
          item1.meta.kauffman_person_last_name,
          item2.meta.kauffman_person_last_name
        ) || PersonListing.sortNames(
          item1.meta.kauffman_person_first_name,
          item2.meta.kauffman_person_first_name
        )
      ) : false
    ));

    // Render markup.
    return (
      <div className="person-listing__inner">
        {! loading && (
          <Fragment>
            <div className="person-listing__toolbar">
              <div className="person-listing__toolbar-inner">
                {0 < departmentList.length && (
                  <Dropdown
                    options={departmentsOptions}
                    onChange={this.onDepartmentActivate}
                    value={activeDepartment}
                  />
                )}
                {0 < this.alphaFilterChunks.length && (
                  <Dropdown
                    options={this.alphaFilterChunks}
                    onChange={this.onAlphaFilterActivate}
                    value={activeAlphaFilter}
                  />
                )}
              </div>
            </div>
            <div className="person__listing">
              {0 === filteredPersons.length && (
                <div>
                  {__('No people in this department.', 'kauffman')}
                </div>
              )}
              {0 < filteredPersons.length && (
                <ul>
                  {filteredPersons.map((person) => {
                    const titleTrimmed = person.title
                      ? DOMPurify.sanitize(person.title.rendered) : '';
                    return (
                      <li
                        key={person.id}
                        className="person-summary"
                      >
                        <a
                          className={
                            classNames(
                              'person-summary__image',
                              {
                                'has-thumbnail': person.featured_media_url,
                              },
                            )
                          }
                          href={person.link}
                        >
                          {(person.featured_media_url) && (
                            <img
                              alt={titleTrimmed}
                              src={person.featured_media_url}
                            />
                          )}
                        </a>
                        <div className="person-summary__content">
                          <a
                            className="person-summary__name"
                            href={person.link}
                          >
                            <RawHTML>
                              {titleTrimmed}
                            </RawHTML>
                          </a>
                          {person.meta.kauffman_person_title && (
                            <div className="person-summary__meta">
                              <div className="person__title">
                                <RawHTML>
                                  {person.meta.kauffman_person_title}
                                </RawHTML>
                              </div>
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </Fragment>
        )}
      </div>
    );
  }
}

if (appElement) {
  ReactDOM.render(React.createElement(PersonListing, null), appElement);
}
